import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Chip,
  FormDialog,
  Grid,
  InputField,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { UpdateClientPayload, actions as clientsActions, selectors as clientsSelectors } from 'store/clients';
import {
  doesFormHaveEmptyValue,
  doesFormHaveInitialValues,
  doesFormHaveError,
  onInputBlur,
  onValueChange,
  getUpdatedResourceWithFormValues
} from 'utils/forms';
import { useStyles } from '../styled';
import { CustomerForm, EditCustomerOverlayProps } from '../types';
import {
  customerSchema,
  getCustomerForm,
  parseRestrictedFonts,
  restrictedFontOptions,
  sortRestrictedFonts
} from '../utils';

const EditCustomerOverlay = (props: EditCustomerOverlayProps) => {
  const { client, onClose } = props;

  const { updateClient } = useActions({
    updateClient: clientsActions.updateClient
  });

  const pending = useSelector(clientsSelectors.selectPending);

  const initialForm = useMemo(() => getCustomerForm(client), [client]);
  const [form, setForm] = useState<CustomerForm>(initialForm);

  const hasEmptyValue = useMemo(() => doesFormHaveEmptyValue(form), [form]);
  const hasError = useMemo(() => doesFormHaveError(form), [form]);
  const hasInitialValues = useMemo(() => doesFormHaveInitialValues(form), [form]);
  const isButtonDisabled = useMemo(() => {
    return hasEmptyValue || hasError || hasInitialValues;
  }, [hasEmptyValue, hasError, hasInitialValues]);

  const selectedRestrictedFonts = useMemo(
    () => parseRestrictedFonts(form.restrictedFonts.value),
    [form.restrictedFonts.value]
  );

  const classes = useStyles();

  const onRestrictedFontsChange = (codes: string[]) => {
    onValueChange(JSON.stringify(sortRestrictedFonts(codes)), 'restrictedFonts', setForm);
  };

  const onSaveClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const updatedClient = getUpdatedResourceWithFormValues(client, form);
    // Specify which keys to update to avoid immutable keys like createdAt and updatedAt
    const payload: UpdateClientPayload = {
      id: updatedClient.id,
      configuration: updatedClient.configuration,
      name: updatedClient.name
    };
    updateClient(payload);
  };

  return (
    <FormDialog
      cancelButtonAction={onClose}
      cancelButtonText="Cancel"
      confirmButtonAction={onSaveClick}
      confirmButtonDisabled={isButtonDisabled}
      confirmButtonIsLoading={pending}
      confirmButtonText="Save"
      open
      title="Edit Customer"
    >
      <Grid className={classes.overlayInnerContainer}>
        {/* INPUT - NAME */}
        <Box className={classes.overlayFieldBox}>
          <InputField
            data-testid="name-input-field"
            error={form.name.error !== null}
            fullWidth
            helperText={form.name.error}
            label="Name"
            onBlur={(e) => onInputBlur(e, 'name', customerSchema, setForm)}
            onChange={(e) => onValueChange(e.target.value, 'name', setForm)}
            size="small"
            value={form.name.value}
          />
        </Box>

        {/* SELECT - RESTRICTED FONTS (OPTIONAL) */}
        <Box className={classes.overlayFieldBox} sx={{ height: 'auto' }}>
          <Typography className={classes.overlayFieldLabel}>Restricted Fonts</Typography>
          <Select
            className={classes.overlayMultiSelect}
            data-testid="restricted-fonts-select"
            multiple
            displayEmpty
            value={selectedRestrictedFonts}
            onChange={(e) => onRestrictedFontsChange(e.target.value as string[])}
            input={<OutlinedInput />}
            renderValue={(value) => {
              const selected = value as string[];
              if (selected.length === 0) {
                return <Typography>Select Restricted Font(s)</Typography>;
              }
              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((code) => (
                    <Chip
                      key={code}
                      label={restrictedFontOptions.find((f) => f.code === code)?.displayName}
                      size="small"
                    />
                  ))}
                </Box>
              );
            }}
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              PaperProps: {
                sx: {
                  mt: 2,
                  maxHeight: 250,
                  overflowY: 'auto',
                  borderRadius: '15px'
                }
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            inputProps={{ 'aria-label': 'Restricted Fonts' }}
          >
            {restrictedFontOptions.map((font) => (
              <MenuItem key={font.code} value={font.code}>
                {font.displayName}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Grid>
    </FormDialog>
  );
};

export default EditCustomerOverlay;
